import { z } from 'zod';
import { CompanyStatuses, Confidences, ContactTypes, SecurityRiskLevels, SourceMethods, SuppressionScopes, SuppressionTypes, } from '../statuses';
import { LIMITS, boolQuery, csvArray, dateQuery, listQuerySchema, optionalTrimmed, trimmed, uuid, } from './common';
const enumOf = (values) => z.enum(values);
/** 企業一覧の絞り込み（docs/05-api-spec.md 第6章 / docs/02-functional-spec.md F05.2） */
export const companyFilterSchema = z.object({
    status: csvArray(enumOf(CompanyStatuses.values)),
    assigneeId: z.string().max(40).optional(), // uuid または 'unassigned'
    tagIds: csvArray(uuid),
    prefecture: z.string().trim().max(20).optional(),
    city: z.string().trim().max(40).optional(),
    industry: z.string().trim().max(60).optional(),
    hasEmail: boolQuery,
    hasForm: boolQuery,
    hasPhone: boolQuery,
    // メール・フォーム・電話のいずれかを持つか（false で「連絡手段なし」）
    hasAnyContact: boolQuery,
    // 直近のセキュリティチェックの判定（複数指定は OR）
    securityRisk: csvArray(enumOf(SecurityRiskLevels.values)),
    // セキュリティチェックの実施有無
    securityChecked: boolQuery,
    excluded: boolQuery,
    hasReply: boolQuery,
    hasDuplicate: boolQuery,
    lowConfidenceOnly: boolQuery,
    lastContactedBefore: dateQuery,
    lastContactedAfter: dateQuery,
    lastCrawledBefore: dateQuery,
    createdAfter: dateQuery,
    createdBefore: dateQuery,
    listId: uuid.optional(),
    collectionRunId: uuid.optional(),
    source: enumOf(['search', 'csv_import', 'manual']).optional(),
});
export const companyListQuerySchema = listQuerySchema.merge(companyFilterSchema);
export const COMPANY_SORT_FIELDS = [
    'updatedAt',
    'createdAt',
    'name',
    'lastContactedAt',
    'lastRepliedAt',
    'status',
];
export const contactSourceSchema = z.object({
    id: uuid,
    field: z.string(),
    sourceUrl: z.string().nullable(),
    method: enumOf(SourceMethods.values),
    excerpt: z.string().nullable(),
    confidence: enumOf(Confidences.values),
    fetchedAt: z.string(),
});
export const contactSchema = z.object({
    id: uuid,
    companyId: uuid,
    storeId: uuid.nullable(),
    type: enumOf(ContactTypes.values),
    value: z.string(),
    valueNormalized: z.string().nullable(),
    label: z.string().nullable(),
    confidence: enumOf(Confidences.values),
    isPrimary: z.boolean(),
    isValid: z.boolean(),
    invalidReason: z.string().nullable(),
    verifiedAt: z.string().nullable(),
    /** フォームの入力欄構成（docs/06-async-jobs.md 4.4） */
    metadata: z.record(z.string(), z.unknown()).nullable(),
    sources: z.array(contactSourceSchema).optional(),
    createdAt: z.string(),
});
export const companySummarySchema = z.object({
    id: uuid,
    name: z.string(),
    domain: z.string().nullable(),
    websiteUrl: z.string().nullable(),
    industry: z.string().nullable(),
    industrySource: z.string().nullable(),
    industryConfidence: enumOf(Confidences.values).nullable(),
    prefecture: z.string().nullable(),
    city: z.string().nullable(),
    status: enumOf(CompanyStatuses.values),
    assignee: z.object({ id: uuid, name: z.string().nullable() }).nullable(),
    isExcluded: z.boolean(),
    excludedReason: z.string().nullable(),
    lastContactedAt: z.string().nullable(),
    lastRepliedAt: z.string().nullable(),
    lastCrawledAt: z.string().nullable(),
    /** セキュリティチェックの直近結果（未実施なら null） */
    securityRisk: enumOf(SecurityRiskLevels.values).nullable(),
    securityCheckedAt: z.string().nullable(),
    hasEmail: z.boolean(),
    hasForm: z.boolean(),
    hasPhone: z.boolean(),
    primaryPhone: z.string().nullable(),
    tags: z.array(z.object({ id: uuid, name: z.string() })),
    hasLowConfidence: z.boolean(),
    duplicateCandidateCount: z.number().int(),
    updatedAt: z.string(),
});
export const companyDetailSchema = companySummarySchema.extend({
    nameNormalized: z.string(),
    postalCode: z.string().nullable(),
    addressLine: z.string().nullable(),
    employeeSize: z.string().nullable(),
    capital: z.number().nullable(),
    foundedOn: z.string().nullable(),
    representative: z.string().nullable(),
    description: z.string().nullable(),
    descriptionConfidence: enumOf(Confidences.values).nullable(),
    source: z.string().nullable(),
    collectionRunId: uuid.nullable(),
    firstSeenAt: z.string().nullable(),
    contacts: z.array(contactSchema),
    stores: z.array(z.object({
        id: uuid,
        name: z.string(),
        prefecture: z.string().nullable(),
        city: z.string().nullable(),
        addressLine: z.string().nullable(),
        phone: z.string().nullable(),
        url: z.string().nullable(),
        sourceUrl: z.string().nullable(),
    })),
    fieldSources: z.record(z.string(), z.array(contactSourceSchema)),
    counts: z.object({
        sendAttempts: z.number().int(),
        inboundMessages: z.number().int(),
        appointments: z.number().int(),
        activities: z.number().int(),
        messages: z.number().int(),
    }),
});
export const createCompanySchema = z.object({
    name: trimmed(LIMITS.name, '企業名を入力してください'),
    websiteUrl: optionalTrimmed(LIMITS.url),
    industry: optionalTrimmed(60),
    postalCode: optionalTrimmed(10),
    prefecture: optionalTrimmed(20),
    city: optionalTrimmed(40),
    addressLine: optionalTrimmed(200),
    phone: optionalTrimmed(30),
    email: optionalTrimmed(254),
    formUrl: optionalTrimmed(LIMITS.url),
    employeeSize: optionalTrimmed(20),
    representative: optionalTrimmed(60),
    description: optionalTrimmed(2000),
    assigneeId: uuid.optional(),
    tagIds: z.array(uuid).max(50).optional(),
});
export const updateCompanySchema = createCompanySchema.partial().extend({
    capital: z.number().int().nonnegative().optional(),
    foundedOn: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .optional(),
});
export const changeStatusSchema = z
    .object({
    status: enumOf(CompanyStatuses.values),
    reason: optionalTrimmed(LIMITS.reason),
})
    // 「失注」「対象外」は理由必須（docs/10-screen-spec.md S31）
    .refine((v) => !['lost', 'excluded'].includes(v.status) || !!v.reason, {
    message: '理由を入力してください',
    path: ['reason'],
});
export const excludeCompanySchema = z.object({
    reason: trimmed(LIMITS.reason, '対象外にする理由を入力してください'),
    /** 同時に除外リストへも登録するか */
    addSuppression: z.boolean().optional(),
    suppressionType: enumOf(SuppressionTypes.values).optional(),
});
export const createContactSchema = z.object({
    type: enumOf(ContactTypes.values),
    value: trimmed(LIMITS.url, '値を入力してください'),
    label: optionalTrimmed(40),
    isPrimary: z.boolean().optional(),
    storeId: uuid.optional(),
});
export const updateContactSchema = z.object({
    value: optionalTrimmed(LIMITS.url),
    label: optionalTrimmed(40),
    isPrimary: z.boolean().optional(),
    isValid: z.boolean().optional(),
    invalidReason: optionalTrimmed(200),
    /** 人が確認した印を付ける（確度が high になる） */
    verified: z.boolean().optional(),
});
export const mergeCompanySchema = z.object({
    sourceCompanyId: uuid,
    /** 項目ごとに 'target'（残す側）か 'source'（統合される側）を選ぶ */
    fieldSelections: z.record(z.string(), z.enum(['target', 'source'])).optional(),
});
export const duplicateCandidateSchema = z.object({
    company: companySummarySchema,
    /** 判定根拠：domain / phone / name */
    matchedBy: z.enum(['domain', 'phone', 'name']),
    similarity: z.number().min(0).max(1),
    reason: z.string(),
});
export const timelineEntrySchema = z.object({
    id: z.string(),
    type: z.enum([
        'send_attempt',
        'inbound_message',
        'appointment',
        'activity',
        'status_change',
        'generated_message',
        'collection',
        'crawl',
    ]),
    occurredAt: z.string(),
    title: z.string(),
    summary: z.string().nullable(),
    /** 状態バッジに使う値（送信結果・返信分類など） */
    badgeKind: z.string().nullable(),
    badgeValue: z.string().nullable(),
    actor: z.object({ id: uuid, name: z.string().nullable() }).nullable(),
    href: z.string().nullable(),
    /** 送信イベントに付く開封・クリック・配信不能などの履歴（日時つきの文） */
    details: z.array(z.string()).optional(),
});
export const bulkAssignSchema = z.object({
    companyIds: z.array(uuid).min(1).max(1000),
    assigneeId: uuid.nullable(),
});
export const bulkTagsSchema = z.object({
    companyIds: z.array(uuid).min(1).max(1000),
    addTagIds: z.array(uuid).max(50).optional(),
    removeTagIds: z.array(uuid).max(50).optional(),
});
export const bulkExcludeSchema = z.object({
    companyIds: z.array(uuid).min(1).max(1000),
    reason: trimmed(LIMITS.reason, '理由を入力してください'),
    exclude: z.boolean().default(true),
});
// ---------------------------------------------------------------- security scan
/** セキュリティチェックの一括実行要求。URL 未設定の企業はスキップする */
export const bulkSecurityScanSchema = z.object({
    companyIds: z.array(uuid).min(1).max(1000),
});
/** セキュリティチェックの実行結果（1 企業の直近分） */
export const securityCheckSchema = z.object({
    id: uuid,
    companyId: uuid,
    riskLevel: enumOf(SecurityRiskLevels.values),
    summary: z.string(),
    findings: z.array(z.object({
        severity: z.enum(['high', 'medium', 'low', 'info']),
        category: z.string().default(''),
        title: z.string(),
        evidence: z.string(),
        note: z.string(),
        /** 推奨対応。2026-09-10 より前の結果には無い */
        recommendation: z.string().optional(),
    })),
    salesHook: z.string().nullable(),
    confidence: enumOf(Confidences.values),
    assumptions: z.array(z.string()),
    /** 受動的に収集した信号（ヘッダ・TLS・Cookie 等）。根拠の確認に使う */
    signals: z.record(z.string(), z.unknown()),
    errorCode: z.string().nullable(),
    errorMessage: z.string().nullable(),
    aiRunId: uuid.nullable(),
    checkedAt: z.string(),
});
// ---------------------------------------------------------------- suppressions
export const suppressionSchema = z.object({
    id: uuid,
    type: enumOf(SuppressionTypes.values),
    scope: enumOf(SuppressionScopes.values),
    value: z.string().nullable(),
    companyId: uuid.nullable(),
    companyName: z.string().nullable(),
    reason: z.string().nullable(),
    source: z.enum(['manual', 'reply_detected', 'bounce', 'import']),
    createdBy: z.object({ id: uuid, name: z.string().nullable() }).nullable(),
    createdAt: z.string(),
    expiresAt: z.string().nullable(),
});
export const createSuppressionSchema = z
    .object({
    type: enumOf(SuppressionTypes.values),
    scope: enumOf(SuppressionScopes.values),
    value: optionalTrimmed(254),
    companyId: uuid.optional(),
    reason: optionalTrimmed(LIMITS.reason),
    expiresAt: z.string().datetime().optional(),
})
    .refine((v) => (v.scope === 'company' ? !!v.companyId : !!v.value), {
    message: '対象を指定してください',
    path: ['value'],
});
export const suppressionListQuerySchema = listQuerySchema.extend({
    type: csvArray(enumOf(SuppressionTypes.values)),
    scope: csvArray(enumOf(SuppressionScopes.values)),
    source: csvArray(enumOf(['manual', 'reply_detected', 'bounce', 'import'])),
});
// ---------------------------------------------------------------- tags
export const tagSchema = z.object({
    id: uuid,
    name: z.string(),
    colorToken: z.string().nullable(),
    companyCount: z.number().int().optional(),
});
export const createTagSchema = z.object({
    name: trimmed(LIMITS.tag, 'タグ名を入力してください'),
    colorToken: optionalTrimmed(30),
    /** 分類基準（企業フィルタと同形）。一致企業へ自動でタグ付けする。null で基準を外す */
    ruleJson: z.record(z.string(), z.unknown()).nullable().optional(),
    /** 基準に一致する企業へ自動でタグを維持するか（不一致になった企業からは外す） */
    autoApply: z.boolean().optional(),
});
/** タグの分類基準に一致する企業数を試算する */
export const tagPreviewSchema = z.object({
    filter: z.record(z.string(), z.unknown()),
});
/** タグの分類基準を今すぐ適用する。sync=true で不一致企業からタグを外す */
export const applyTagSchema = z.object({
    sync: z.boolean().optional(),
});
/** 自然言語のテキストから分類基準（企業フィルタ）を推定する */
export const tagParseSchema = z.object({
    text: z.string().trim().min(1, '条件の文章を入力してください').max(500),
});
// ---------------------------------------------------------------- CSV
export const exportCompaniesSchema = z.object({
    filter: companyFilterSchema.optional(),
    q: z.string().trim().max(200).optional(),
    columns: z.array(z.string().max(40)).max(60).optional(),
});
export const importAnalyzeResultSchema = z.object({
    importId: uuid,
    fileName: z.string(),
    totalRows: z.number().int(),
    preview: z.array(z.record(z.string(), z.string())),
    headers: z.array(z.string()),
    /** 列名から推定したマッピング候補 */
    suggestedMapping: z.record(z.string(), z.string()),
    validation: z.object({
        importable: z.number().int(),
        duplicate: z.number().int(),
        error: z.number().int(),
        errors: z.array(z.object({ row: z.number().int(), reason: z.string() })).max(100),
    }),
});
export const importExecuteSchema = z.object({
    importId: uuid,
    mapping: z.record(z.string(), z.string()),
    dedupeStrategy: z.enum(['domain', 'phone', 'none']).default('domain'),
    assigneeId: uuid.optional(),
    tagIds: z.array(uuid).max(50).optional(),
});
export const IMPORT_COLUMNS = [
    { key: 'name', label: '企業名', required: true },
    { key: 'websiteUrl', label: 'URL', required: false },
    { key: 'industry', label: '業種', required: false },
    { key: 'postalCode', label: '郵便番号', required: false },
    { key: 'prefecture', label: '都道府県', required: false },
    { key: 'city', label: '市区町村', required: false },
    { key: 'addressLine', label: '住所', required: false },
    { key: 'phone', label: '電話番号', required: false },
    { key: 'email', label: 'メールアドレス', required: false },
    { key: 'formUrl', label: 'フォーム URL', required: false },
    { key: 'employeeSize', label: '従業員数', required: false },
    { key: 'representative', label: '代表者', required: false },
    { key: 'description', label: '事業内容', required: false },
    { key: 'tags', label: 'タグ', required: false },
    { key: 'note', label: 'メモ', required: false },
];
export const EXPORT_COLUMNS = [
    { key: 'name', label: '企業名' },
    { key: 'domain', label: 'ドメイン' },
    { key: 'websiteUrl', label: 'URL' },
    { key: 'industry', label: '業種' },
    { key: 'prefecture', label: '都道府県' },
    { key: 'city', label: '市区町村' },
    { key: 'addressLine', label: '住所' },
    { key: 'phone', label: '電話番号' },
    { key: 'email', label: 'メールアドレス' },
    { key: 'formUrl', label: 'フォーム URL' },
    { key: 'status', label: '営業状況' },
    { key: 'assignee', label: '担当' },
    { key: 'tags', label: 'タグ' },
    { key: 'lastContactedAt', label: '最終接触' },
    { key: 'lastRepliedAt', label: '最終返信' },
    { key: 'lastCrawledAt', label: '最終クロール' },
    { key: 'isExcluded', label: '対象外' },
    { key: 'createdAt', label: '登録日時' },
];
//# sourceMappingURL=companies.js.map